import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { useRfpPipeline, useRfpStats, useUpdateRfpStage } from '@/hooks/use-api'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { FileText, Plus, Loader2, Calendar, Search } from 'lucide-react'
import { formatCurrency, toDisplayDate } from '@/lib/utils'
import { CreateRfpModal } from '@/components/CreateRfpModal'
import type { RfpStage } from '@/types'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  pointerWithin,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

const STAGES: { key: RfpStage; label: string; bgColor: string; borderColor: string }[] = [
  {
    key: 'Qualify',
    label: 'Qualify',
    bgColor: CATEGORICAL.navy.tint,
    borderColor: CATEGORICAL.navy.solid,
  },
  {
    key: 'In Progress',
    label: 'In Progress',
    bgColor: CATEGORICAL.pollen.tint,
    borderColor: GRACKLE_COLORS.prussianBlue,
  },
  {
    key: 'Submitted',
    label: 'Submitted',
    bgColor: CATEGORICAL.plum.tint,
    borderColor: CATEGORICAL.plum.solid,
  },
  {
    key: 'Shortlisted',
    label: 'Shortlisted',
    bgColor: CATEGORICAL.clay.tint,
    borderColor: CATEGORICAL.clay.solid,
  },
  {
    key: 'Negotiations',
    label: 'Negotiations',
    bgColor: CATEGORICAL.steel.tint,
    borderColor: CATEGORICAL.steel.solid,
  },
  {
    key: 'Won',
    label: 'Won',
    bgColor: CATEGORICAL.evergreen.tint,
    borderColor: CATEGORICAL.evergreen.solid,
  },
  {
    key: 'Lost',
    label: 'Lost',
    bgColor: CATEGORICAL.raspberry.tint,
    borderColor: CATEGORICAL.raspberry.solid,
  },
  {
    key: 'Dismissed',
    label: 'Dismissed',
    bgColor: CATEGORICAL.slate.tint,
    borderColor: CATEGORICAL.slate.solid,
  },
]

interface DraggableRfpCardProps {
  rfp: any
  stage: RfpStage
}

function DraggableRfpCard({ rfp, stage }: DraggableRfpCardProps) {
  // The /rfps/pipeline endpoint's card payload does NOT include a `stage`
  // field on each RFP — stage lives on the parent bucket — so we take the
  // column's stage as a prop rather than reading `rfp.stage`.
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: rfp.id,
    data: { stage },
  })

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="listitem"
      tabIndex={0}
      aria-describedby="rfp-kanban-keyboard-help"
      className="bg-background rounded-md border p-2.5 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring touch-none"
    >
      <Link
        to={`/rfps/${rfp.id}`}
        className="block font-semibold text-xs leading-tight truncate hover:underline"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {rfp.name}
      </Link>
      {rfp.account_name && (
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">{rfp.account_name}</p>
      )}
      {rfp.estimated_value ? (
        <p className="text-xs font-medium mt-1.5" style={{ color: CATEGORICAL.evergreen.solid }}>
          {formatCurrency(rfp.estimated_value_cad ?? rfp.estimated_value, 'CAD')}
        </p>
      ) : null}
      {rfp.submission_deadline && (
        <div className="flex items-center gap-1 mt-1">
          <Calendar className="h-2.5 w-2.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-[11px] text-muted-foreground">
            Due{' '}
            {(
              toDisplayDate(rfp.submission_deadline) ?? new Date(rfp.submission_deadline)
            ).toLocaleDateString()}
          </span>
        </div>
      )}
      {rfp.task_count > 0 && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[11px] text-muted-foreground">
            Tasks: {rfp.tasks_complete}/{rfp.task_count}
          </span>
          {rfp.tasks_overdue > 0 && (
            <span className="text-[10px] text-destructive font-medium">
              {rfp.tasks_overdue} overdue
            </span>
          )}
        </div>
      )}
      {rfp.bid_number && (
        <p className="text-[10px] text-muted-foreground/70 mt-1">Bid #{rfp.bid_number}</p>
      )}
    </div>
  )
}

interface DroppableStageColumnProps {
  stage: { key: RfpStage; label: string; bgColor: string; borderColor: string }
  rfps: any[]
}

function DroppableStageColumn({ stage, rfps }: DroppableStageColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${stage.key}`,
    data: { type: 'column', stage: stage.key },
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        backgroundColor: stage.bgColor,
        borderColor: stage.borderColor,
      }}
      className={`flex-1 min-w-0 rounded-lg border-2 p-2 transition-[box-shadow] ${
        isOver ? 'ring-2 ring-primary ring-offset-1' : ''
      }`}
      role="list"
      aria-label={`${stage.label} stage`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-xs">{stage.label}</h3>
        <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
          {rfps.length}
        </Badge>
      </div>

      <div className="space-y-1.5 min-h-[60px]">
        {rfps.map((rfp: any) => (
          <DraggableRfpCard key={rfp.id} rfp={rfp} stage={stage.key} />
        ))}
        {rfps.length === 0 && (
          <p className="text-[10px] text-muted-foreground text-center py-3">No RFPs</p>
        )}
      </div>
    </div>
  )
}

export default function Rfps() {
  const { data: pipeline, isLoading } = useRfpPipeline()
  const { data: stats } = useRfpStats()
  const updateStage = useUpdateRfpStage()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [search, setSearch] = useState('')

  const pipelineMap = new Map((pipeline || []).map((s) => [s.stage, s]))

  // @dnd-kit sensors. PointerSensor activation distance lets card-name clicks
  // still navigate without being captured as a drag-start.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  )

  const handleStageChange = async (rfpId: string, newStage: RfpStage) => {
    const queryKey = ['rfps', 'pipeline'] as const
    const previous = queryClient.getQueryData(queryKey)

    // Optimistic move between stage buckets
    queryClient.setQueryData(queryKey, (old: any) => {
      if (!Array.isArray(old)) return old
      let moved: any = null
      const withoutCard = old.map((bucket: any) => {
        if (!bucket?.rfps) return bucket
        const keep = bucket.rfps.filter((r: any) => {
          if (r.id === rfpId) {
            moved = { ...r, stage: newStage }
            return false
          }
          return true
        })
        return { ...bucket, rfps: keep }
      })
      if (!moved) return old

      // If the destination bucket already exists, prepend the card; otherwise
      // add a new bucket so the card shows up even if the stage wasn't in the
      // original payload (rare but defensive).
      const hasDest = withoutCard.some((b: any) => b.stage === newStage)
      const withCard = hasDest
        ? withoutCard.map((b: any) =>
            b.stage === newStage ? { ...b, rfps: [moved, ...(b.rfps || [])] } : b
          )
        : [...withoutCard, { stage: newStage, rfps: [moved] }]
      return withCard
    })

    try {
      await updateStage.mutateAsync({ id: rfpId, stage: newStage })
      queryClient.invalidateQueries({ queryKey: ['rfps'] })
    } catch (e) {
      queryClient.setQueryData(queryKey, previous)
      const message =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e as Error)?.message ||
        'Failed to update stage'
      toast({ title: 'Failed to update stage', description: message, variant: 'destructive' })
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const sourceStage = (active.data.current as { stage?: RfpStage } | undefined)?.stage
    const overData = over.data.current as { type?: string; stage?: RfpStage } | undefined
    const destStage = overData?.type === 'column' ? overData.stage : overData?.stage
    if (!destStage || !sourceStage || sourceStage === destStage) return
    await handleStageChange(active.id as string, destStage)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="px-2 py-1">
              <FileText className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              RFP
            </Badge>
            <h1 className="text-3xl font-bold tracking-tight">RFPs</h1>
          </div>
          <p className="text-muted-foreground">Track and manage Requests for Proposal</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} aria-label="Create new RFP">
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          New RFP
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total RFPs</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {parseInt(stats.identify || 0) +
                  parseInt(stats.qualify || 0) +
                  parseInt(stats.in_progress || 0) +
                  parseInt(stats.submitted || 0) +
                  parseInt(stats.shortlisted || 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Pipeline Value</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(parseFloat(stats.pipeline_value || 0))}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Won</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold" style={{ color: CATEGORICAL.evergreen.solid }}>
                {stats.won || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Dismissed</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-muted-foreground">{stats.dismissed || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            placeholder="Search RFPs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            aria-label="Search RFPs"
          />
        </div>
      </div>

      {/* Kanban Board */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <span className="ml-2">Loading pipeline...</span>
        </div>
      ) : (
        <section role="region" aria-label="RFPs by stage">
          {/* Keyboard usage help (visually hidden) */}
          <p id="rfp-kanban-keyboard-help" className="sr-only">
            Drag to another stage, or use keyboard: Space to pick up, Arrow keys to move, Space to
            drop, Escape to cancel.
          </p>

          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            collisionDetection={pointerWithin}
          >
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-2">
                {STAGES.map((stage) => {
                  const stageData = pipelineMap.get(stage.key)
                  const rfps = (stageData?.rfps || []).filter(
                    (r: any) =>
                      !search ||
                      r.name?.toLowerCase().includes(search.toLowerCase()) ||
                      r.bid_number?.toLowerCase().includes(search.toLowerCase())
                  )
                  return <DroppableStageColumn key={stage.key} stage={stage} rfps={rfps} />
                })}
              </div>
            </div>
          </DndContext>
        </section>
      )}

      <CreateRfpModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
    </div>
  )
}
